import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Award, CreditCard, MessageSquare, Bell, CheckCircle, AlertCircle, ArrowRight } from "lucide-react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const CARD_ACCENTS = ["#0A7AFF", "#22C55E", "#FF3636", "#8B5CF6"];

const recentGrades = [
  { subject: "Data Structures", grade: "A+", marks: "96/100", date: "Jan 20" },
  { subject: "Computer Networks", grade: "A", marks: "88/100", date: "Jan 18" },
  { subject: "DBMS", grade: "A+", marks: "92/100", date: "Jan 15" },
];

const notifications = [
  { text: "Attendance below 75% in Operating Systems", type: "warning", time: "Today" },
  { text: "Assignment submitted: Data Structures Unit 3", type: "success", time: "Yesterday" },
  { text: "Fee receipt generated for Sem 5", type: "info", time: "2 days ago" },
];

export default function ParentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dashboard/stats`, { withCredentials: true })
      .then(r => setStats(r.data)).catch(() => {});
  }, []);

  const statCards = [
    { label: "Attendance", value: `${stats?.stats?.child_attendance || 87.5}%`, sub: "Current sem" },
    { label: "Child's CGPA", value: stats?.stats?.child_cgpa || "8.4", sub: "All semesters" },
    { label: "Fees Due", value: `₹24.5K`, sub: "Pay before Mar 31" },
    { label: "Messages", value: stats?.stats?.unread_messages || 2, sub: "Unread" },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} data-testid="parent-dashboard" className="space-y-6">
      <div className="border-b border-[#D8D4CB] pb-5">
        <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Parent Portal</p>
        <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">Hello, {user?.name?.split(" ")[0]}.</h2>
      </div>

      {/* Child banner */}
      <div className="bg-[#0C0C10] p-6 flex items-center justify-between" data-testid="child-banner">
        <div>
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#EAFF00] mb-2">Your Child</p>
          <p className="font-['Syne'] font-bold text-xl text-[#EEEADF]">Arjun Sharma</p>
          <p className="font-['DM_Sans'] text-sm text-[#EEEADF]/40 mt-0.5">Roll No: CSE22047 — Sem 5 — Computer Science</p>
        </div>
        <div className="w-14 h-14 bg-[#EAFF00] flex items-center justify-center flex-shrink-0">
          <span className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">A</span>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <motion.div
            key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            data-testid={`parent-stat-${i}`}
            className="bg-white border border-[#E2DDD4] p-5 hover:border-[#0C0C10] transition-colors"
            style={{ borderLeft: `3px solid ${CARD_ACCENTS[i]}` }}
          >
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-3">{s.label}</p>
            <p className="font-['JetBrains_Mono'] font-bold text-[2.2rem] text-[#0C0C10] leading-none">{s.value}</p>
            <p className="font-['DM_Sans'] text-xs text-[#7A7468] mt-3">{s.sub}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent grades */}
        <div className="bg-white border border-[#E2DDD4] p-6" data-testid="recent-grades">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-4">Recent Results</p>
          <div className="space-y-2">
            {recentGrades.map((g, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-[#F5F2EA] border border-[#E2DDD4]">
                <div className="w-10 h-10 bg-[#22C55E]/10 border border-[#22C55E]/20 flex items-center justify-center flex-shrink-0">
                  <span className="font-['JetBrains_Mono'] font-bold text-sm text-[#22C55E]">{g.grade}</span>
                </div>
                <div className="flex-1">
                  <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{g.subject}</p>
                  <p className="font-['DM_Sans'] text-xs text-[#7A7468]">{g.marks} — {g.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white border border-[#E2DDD4] p-6" data-testid="parent-notifications">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-4">Notifications</p>
          <div className="space-y-3">
            {notifications.map((n, i) => (
              <div key={i} className="flex items-start gap-3 pb-3 border-b border-[#F0ECE4] last:border-0">
                <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  n.type === "warning" ? "bg-[#F59E0B]/10" : n.type === "success" ? "bg-[#22C55E]/10" : "bg-[#0A7AFF]/10"
                }`}>
                  {n.type === "warning" ? <AlertCircle className="w-3.5 h-3.5 text-[#F59E0B]" /> :
                   n.type === "success" ? <CheckCircle className="w-3.5 h-3.5 text-[#22C55E]" /> :
                   <Bell className="w-3.5 h-3.5 text-[#0A7AFF]" />}
                </div>
                <div>
                  <p className="font-['DM_Sans'] text-sm text-[#0C0C10]">{n.text}</p>
                  <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-widest text-[#BDBAA8] mt-0.5">{n.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Fee payment CTA */}
      <div className="bg-[#FF3636]/5 border border-[#FF3636]/20 p-5 flex items-center justify-between" data-testid="fee-payment-cta">
        <div>
          <p className="font-['Syne'] font-bold text-sm text-[#0C0C10]">Fee Payment Due</p>
          <p className="font-['DM_Sans'] text-xs text-[#7A7468] mt-0.5">₹24,500 — Semester 5 — Due by March 31, 2026</p>
        </div>
        <button
          onClick={() => navigate("/dashboard/fees")}
          data-testid="pay-now-btn"
          className="flex items-center gap-2 bg-[#FF3636] text-white font-['Syne'] font-bold text-xs uppercase tracking-wider px-5 py-3 hover:bg-[#CC2020] transition-colors"
        >
          Pay Now <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </motion.div>
  );
}
